import { Group, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconCircleX } from '@tabler/icons-react';
import type { FlowIssue } from '@/widgets/flow-validation';

function IssueIcon({ level }: { level: FlowIssue['level'] }) {
  return level === 'error' ? (
    <IconCircleX size={16} color="var(--mantine-color-red-6)" style={{ flexShrink: 0 }} />
  ) : (
    <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" style={{ flexShrink: 0 }} />
  );
}

function IssueMessage({ issue }: { issue: FlowIssue }) {
  return (
    <Group gap="xs" wrap="nowrap" py={4} px={4} style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
      <IssueIcon level={issue.level} />
      <Text size="sm" style={{ lineHeight: 1.3 }}>
        {issue.message}
      </Text>
    </Group>
  );
}

function FlowIssueLine({ issue }: { issue: FlowIssue }) {
  return (
    <Group gap="xs" wrap="nowrap" py={4}>
      <IssueIcon level={issue.level} />
      <Text size="sm" style={{ lineHeight: 1.3 }}>
        {issue.message}
      </Text>
    </Group>
  );
}

function NodeIssueLine({
  issue,
  nodeId,
  onSelectNode,
}: {
  issue: FlowIssue;
  nodeId: string;
  onSelectNode: (nodeId: string) => void;
}) {
  return (
    <UnstyledButton onClick={() => onSelectNode(nodeId)} style={{ width: '100%' }}>
      <IssueMessage issue={issue} />
    </UnstyledButton>
  );
}

type IssuesListProps = {
  issues: FlowIssue[];
  onSelectNode: (nodeId: string) => void;
  levelFilter?: FlowIssue['level'];
  maxHeight?: number;
};

export function IssuesList({ issues, onSelectNode, levelFilter, maxHeight = 300 }: IssuesListProps) {
  const filtered = levelFilter ? issues.filter((i) => i.level === levelFilter) : issues;

  if (filtered.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No issues
      </Text>
    );
  }

  return (
    <ScrollArea.Autosize mah={maxHeight}>
      <Stack gap={2}>
        {filtered.map((issue, i) =>
          issue.nodeId ? (
            <NodeIssueLine key={i} issue={issue} nodeId={issue.nodeId} onSelectNode={onSelectNode} />
          ) : (
            <FlowIssueLine key={i} issue={issue} />
          ),
        )}
      </Stack>
    </ScrollArea.Autosize>
  );
}
